import { getTrainingProgress } from '@metaonia/api-client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getStartedModuleIds } from './use-started-modules';

import type { GetTrainingProgress200, TrainingProgress, ModuleProgress } from '@metaonia/api-client';

// Re-export types from generated client for consumers
export type { TrainingProgress, ModuleProgress };

/** Module progress with an additional 'in_progress' status for locally-started modules. */
export type EnrichedModuleProgress = Omit<ModuleProgress, 'status'> & {
  status: ModuleProgress['status'] | 'in_progress';
};

/** Training progress with enriched module statuses. */
export type EnrichedTrainingProgress = Omit<TrainingProgress, 'modules'> & {
  modules: EnrichedModuleProgress[];
};

export function useTrainingProgress() {
  return useQuery({
    queryKey: ['training-progress'],
    queryFn: async () => {
      const response = await getTrainingProgress();
      return (response as GetTrainingProgress200).data;
    },
    staleTime: 0,
  });
}

export function useEnrichedProgress() {
  const query = useTrainingProgress();

  const data = useMemo((): EnrichedTrainingProgress | undefined => {
    if (!query.data) return undefined;
    const startedIds = getStartedModuleIds();
    return {
      ...query.data,
      modules: query.data.modules.map((m): EnrichedModuleProgress => {
        if (m.status === 'not_started' && startedIds.has(m.module_id)) {
          return { ...m, status: 'in_progress' as const };
        }
        return m;
      }),
    };
  }, [query.data]);

  return { ...query, data };
}
